import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { LoadingState } from '@/components/Spinner';
import { PdfCanvasViewer, type PdfLoadState } from './PdfCanvasViewer';
import {
  OVERALL_ANALYSIS_PDF_MAX_BYTES,
  type OverallAnalysisResponse,
} from '@/types/overallAnalysis';

interface OverallAnalysisPanelProps {
  sessionId: string;
  isAdmin: boolean;
  sessionStatus?: string | null;
  markReadOnLoad?: boolean;
}

// 文字起こしDLは文字起こし完了後いつでも可（backend の
// TRANSCRIPT_NOT_READY_STATUSES と対応）。
const TRANSCRIPT_NOT_READY_STATUSES = new Set([
  'transcribing',
  'transcribing_failed',
]);
const DOWNLOAD_LOCK_HINT = '文字起こしが完了するとダウンロードできます';

const ACCEPTED_PDF_UPLOAD_TYPES = '.pdf,application/pdf';

function pickContentDispositionFilename(disposition: string): string | null {
  const starMatch = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(disposition);
  if (starMatch?.[2]) {
    try {
      return decodeURIComponent(starMatch[2].trim());
    } catch {
      // fall through to ASCII filename
    }
  }
  const asciiMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return asciiMatch?.[1]?.trim() ?? null;
}

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as {
      userMessage?: unknown;
      response?: { data?: { detail?: unknown; message?: unknown } };
      message?: unknown;
    };
    if (typeof e.userMessage === 'string' && e.userMessage) return e.userMessage;
    const detail = e.response?.data?.detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const m = (detail as { message?: unknown }).message;
      if (typeof m === 'string' && m) return m;
    }
    if (typeof detail === 'string' && detail) return detail;
    const dataMessage = e.response?.data?.message;
    if (typeof dataMessage === 'string' && dataMessage) return dataMessage;
    if (typeof e.message === 'string' && e.message && !e.message.startsWith('Request failed')) {
      return e.message;
    }
  }
  return fallback;
}

export function OverallAnalysisPanel({
  sessionId,
  isAdmin,
  sessionStatus,
  markReadOnLoad = false,
}: OverallAnalysisPanelProps) {
  const transcriptReady = sessionStatus
    ? !TRANSCRIPT_NOT_READY_STATUSES.has(sessionStatus)
    : false;
  const transcriptDownloadDisabledReason = !transcriptReady ? DOWNLOAD_LOCK_HINT : null;
  const [analysis, setAnalysis] = useState<OverallAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [readMarkWarning, setReadMarkWarning] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfViewing, setPdfViewing] = useState(false);
  const [pdfExpanded, setPdfExpanded] = useState(false);
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);
  const [pdfViewError, setPdfViewError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  // 承認は「中身を見たこと」が前提の操作なので、PDFが実際に描画できるまで押させない。
  const [pdfRenderState, setPdfRenderState] = useState<PdfLoadState>('loading');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setReadMarkWarning(null);
    try {
      const resp = await api.get<OverallAnalysisResponse>(
        `/sessions/${sessionId}/overall-analysis`,
      );
      setAnalysis(resp.data);
    } catch (err) {
      setLoadError(apiErrorMessage(err, '全体分析の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void fetchAnalysis();
  }, [fetchAnalysis]);

  useEffect(() => {
    if (
      !markReadOnLoad ||
      !analysis?.artifacts_completed_at ||
      analysis.analysis_revision <= 0
    ) return;

    let cancelled = false;
    void api.put(`/sessions/${sessionId}/overall-analysis/read`, {
      analysis_revision: analysis.analysis_revision,
    }).catch(() => {
      if (!cancelled) {
        setReadMarkWarning(
          '全体分析を表示しましたが、既読に更新できませんでした。未読のまま扱われます。',
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [analysis, markReadOnLoad, sessionId]);

  const handleDownloadTranscript = useCallback(async () => {
    setActionError(null);
    setActionMessage(null);
    setDownloading(true);
    try {
      const resp = await api.get<Blob>(`/sessions/${sessionId}/transcript.txt`, {
        responseType: 'blob',
      });
      const disposition = (resp.headers['content-disposition'] ?? '') as string;
      const filename = pickContentDispositionFilename(disposition) ?? `${sessionId}_transcript.txt`;
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(apiErrorMessage(err, '文字起こしのダウンロードに失敗しました'));
    } finally {
      setDownloading(false);
    }
  }, [sessionId]);

  const handlePdfUploadClick = useCallback(() => {
    setActionError(null);
    setActionMessage(null);
    pdfInputRef.current?.click();
  }, []);

  const handlePdfSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setActionError('PDFファイルを選択してください');
        return;
      }
      if (file.size <= 0 || file.size > OVERALL_ANALYSIS_PDF_MAX_BYTES) {
        setActionError('PDFは20MB以下にしてください');
        return;
      }
      if (analysis?.pdf) {
        const ok = window.confirm(
          '既に登録されているPDFを上書きします。よろしいですか?\n（上書き前のPDFは復元できません）',
        );
        if (!ok) return;
      }
      setPdfUploading(true);
      setActionError(null);
      setActionMessage(null);
      try {
        const presign = await api.post<{
          upload_url: string;
          s3_key: string;
          content_type: string;
        }>(`/sessions/${sessionId}/overall-analysis/pdf/upload-url`, {
          filename: file.name,
        });
        const uploadResponse = await fetch(presign.data.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': presign.data.content_type },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error('PDFのストレージ送信に失敗しました');
        }
        const completed = await api.post<OverallAnalysisResponse>(
          `/sessions/${sessionId}/overall-analysis/pdf/complete`,
          {
            s3_key: presign.data.s3_key,
            filename: file.name,
          },
        );
        setAnalysis(completed.data);
        setActionMessage(`PDFをアップロードしました（${file.name}）`);
      } catch (err) {
        setActionError(apiErrorMessage(err, 'PDFのアップロードに失敗しました'));
      } finally {
        setPdfUploading(false);
      }
    },
    [analysis?.pdf, sessionId],
  );

  const handleApprove = useCallback(async () => {
    const revision = analysis?.analysis_revision;
    if (revision === undefined) return;
    const ok = window.confirm(
      'この全体分析PDFを承認します。よろしいですか?\n'
      + '承認すると、担当クライアントがPDFを閲覧・ダウンロードできるようになります。\n'
      + '承認後に取り消すことはできません。',
    );
    if (!ok) return;
    setApproving(true);
    setActionError(null);
    setActionMessage(null);
    try {
      // 表示中のPDFのrevisionを送る。差し替えられていれば backend が 409 で弾き、
      // 見ていない内容を承認してしまうことを防ぐ。
      const resp = await api.post<OverallAnalysisResponse>(
        `/sessions/${sessionId}/overall-analysis/approve`,
        { analysis_revision: revision },
      );
      setAnalysis(resp.data);
      setActionMessage('承認しました。クライアントに公開されます。');
    } catch (err) {
      const message = apiErrorMessage(err, '承認に失敗しました');
      // 他の担当者が先に承認した／PDFが差し替わった場合に備えて取り直す
      let latest: OverallAnalysisResponse | null = null;
      try {
        const resp = await api.get<OverallAnalysisResponse>(
          `/sessions/${sessionId}/overall-analysis`,
        );
        latest = resp.data;
        setAnalysis(latest);
      } catch {
        // 再取得に失敗した場合は取得前の表示のまま、エラーだけ出す
      }
      if (latest && !latest.approval_pending) {
        // 既に承認済みへ追従できたなら、赤いエラーではなく事実を伝える
        setActionError(null);
        setActionMessage(
          latest.approved_by?.name
            ? `既に ${latest.approved_by.name} さんが承認済みです。`
            : '既に承認済みです。',
        );
      } else {
        setActionError(message);
      }
    } finally {
      setApproving(false);
    }
  }, [analysis?.analysis_revision, sessionId]);

  const loadPdfViewUrl = useCallback(async () => {
    setPdfViewing(true);
    setPdfViewUrl(null);
    setPdfViewError(null);
    setPdfRenderState('loading');
    try {
      const resp = await api.get<{ url: string }>(
        `/sessions/${sessionId}/overall-analysis/pdf/view-url`,
      );
      setPdfViewUrl(resp.data.url);
    } catch (err) {
      setPdfViewError(apiErrorMessage(err, 'PDFを表示できませんでした'));
    } finally {
      setPdfViewing(false);
    }
  }, [sessionId]);

  useEffect(() => {
    setPdfViewUrl(null);
    setPdfViewError(null);
    if (analysis?.pdf) {
      void loadPdfViewUrl();
    }
  }, [analysis?.pdf, loadPdfViewUrl]);

  useEffect(() => {
    if (!analysis?.pdf) {
      setPdfExpanded(false);
    }
  }, [analysis?.pdf, sessionId]);

  useEffect(() => {
    if (!pdfExpanded) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPdfExpanded(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [pdfExpanded]);

  const handleDownloadPdf = useCallback(async () => {
    setPdfDownloading(true);
    setActionError(null);
    try {
      const resp = await api.get<Blob>(
        `/sessions/${sessionId}/overall-analysis/pdf/download`,
        { responseType: 'blob' },
      );
      const disposition = (resp.headers['content-disposition'] ?? '') as string;
      const filename = pickContentDispositionFilename(disposition) ?? analysis?.pdf?.filename ?? 'analysis.pdf';
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(apiErrorMessage(err, 'PDFをダウンロードできませんでした'));
    } finally {
      setPdfDownloading(false);
    }
  }, [analysis?.pdf?.filename, sessionId]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
        <LoadingState message="全体分析を読み込み中..." />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 p-8 text-sm text-amber-800">
        {loadError}
      </div>
    );
  }

  const approvalPending = Boolean(analysis?.approval_pending);
  const canApprove = Boolean(analysis?.can_approve);
  // 承認待ちで PDF が返らないのは、開示を止められている担当クライアント本人だけ。
  const awaitingDisclosure = approvalPending && !analysis?.pdf;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6">
      {readMarkWarning && (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {readMarkWarning}
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">全体分析</h2>
          <p className="mt-1 text-xs text-gray-500">
            セッション全体を対象に作成された分析・評価コメント
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDownloadTranscript}
                disabled={downloading || !transcriptReady}
                title={transcriptDownloadDisabledReason ?? undefined}
                aria-describedby={!transcriptReady ? 'transcript-download-lock-hint' : undefined}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading ? 'ダウンロード中...' : '文字起こしをダウンロード'}
              </button>
              <button
                type="button"
                onClick={handlePdfUploadClick}
                disabled={pdfUploading}
                className="rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pdfUploading
                  ? 'PDFアップロード中...'
                  : analysis?.pdf
                    ? 'PDFを再アップロード'
                    : 'PDFをアップロード'}
              </button>
              <input
                ref={pdfInputRef}
                type="file"
                accept={ACCEPTED_PDF_UPLOAD_TYPES}
                onChange={handlePdfSelected}
                className="hidden"
                data-testid="overall-analysis-pdf-input"
              />
            </div>
            {!transcriptReady && (
              <p
                id="transcript-download-lock-hint"
                className="text-xs text-amber-700"
              >
                文字起こしは文字起こし完了後にダウンロードできます
              </p>
            )}
          </div>
        )}
      </div>

      {actionError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {actionError}
        </div>
      )}
      {actionMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {actionMessage}
        </div>
      )}

      {approvalPending && !awaitingDisclosure && (
        <div className="rounded-md border border-violet-200 bg-violet-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-violet-900">
                {canApprove ? '全体分析の承認をお願いします' : '担当フィードバッカーの承認待ちです'}
              </p>
              <p className="mt-1 text-sm text-violet-800">
                {canApprove
                  ? 'このPDFはまだクライアントに公開されていません。他社の情報が含まれていないかを確認したうえで承認してください。'
                  : '担当フィードバッカーが内容を確認して承認すると、クライアントに公開されます。'}
              </p>
            </div>
            {canApprove && (
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={approving || pdfRenderState !== 'loaded'}
                  title={pdfRenderState !== 'loaded' ? 'PDFを表示してから承認できます' : undefined}
                  className="rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {approving ? '承認処理中...' : 'この内容で承認する'}
                </button>
                {pdfRenderState !== 'loaded' && (
                  <p className="text-xs text-violet-800">
                    {pdfRenderState === 'error'
                      ? 'PDFを表示できないため承認できません'
                      : 'PDFの表示を待っています'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-4 overflow-auto">
        {/* amber はこのパネルではエラー・警告の色。正常な待機工程なので
            ステータスバッジと同じ violet に揃える。 */}
        {awaitingDisclosure && (
          <div className="rounded-md border border-violet-200 bg-violet-50 p-6 text-center">
            <p className="text-sm text-violet-900">
              全体分析は担当フィードバッカーの確認後に公開されます。
            </p>
          </div>
        )}

        {analysis?.pdf && (
          <>
          {pdfExpanded && (
            <button
              type="button"
              aria-label="拡大表示の背景を閉じる"
              onClick={() => setPdfExpanded(false)}
              className="fixed inset-0 z-40 cursor-default bg-black/60"
            />
          )}
          <section
            id="overall-analysis-pdf-panel"
            className={[
              'border border-indigo-100 bg-indigo-50 p-4',
              pdfExpanded
                ? 'fixed inset-3 z-50 flex min-h-0 flex-col overflow-hidden rounded-lg shadow-2xl sm:inset-6'
                : 'rounded-md',
            ].join(' ')}
          >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">全体分析PDF</h3>
              <p className="mt-1 text-sm text-gray-700">{analysis.pdf.filename}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                aria-label={pdfExpanded ? '拡大表示を閉じる' : 'PDFを拡大表示'}
                onClick={() => setPdfExpanded((current) => !current)}
                className="rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
              >
                {pdfExpanded ? '元のサイズに戻す' : '拡大表示'}
              </button>
              <button
                type="button"
                onClick={() => void loadPdfViewUrl()}
                disabled={pdfViewing}
                className="rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
              >
                {pdfViewing ? '読み込み中...' : 'PDFを再読み込み'}
              </button>
              {analysis.pdf.can_download && (
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={pdfDownloading}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {pdfDownloading ? 'ダウンロード中...' : 'PDFをダウンロード'}
                </button>
              )}
            </div>
          </div>
          {analysis.pdf.password_setup_required && (
            <p className="mt-3 text-xs text-amber-700">
              ダウンロードするには、
              <a href="/settings" className="font-medium underline">ユーザー設定でPDFパスワードを設定</a>
              してください。閲覧はパスワード未設定でも可能です。
            </p>
          )}
            <div
              data-testid="overall-analysis-pdf-viewer"
              className={[
                'mt-4 flex items-center justify-center overflow-hidden rounded-md border border-indigo-200 bg-white',
                pdfExpanded
                  ? 'min-h-0 flex-1'
                  : 'h-[82vh] min-h-[40rem] max-h-[72rem]',
              ].join(' ')}
            >
              {pdfViewing && <LoadingState message="PDFを読み込み中..." />}
              {!pdfViewing && pdfViewError && (
                <div className="px-6 text-center">
                  <p className="text-sm text-red-700">{pdfViewError}</p>
                  <button
                    type="button"
                    onClick={() => void loadPdfViewUrl()}
                    className="mt-3 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                  >
                    もう一度読み込む
                  </button>
                </div>
              )}
              {!pdfViewing && pdfViewUrl && (
                <PdfCanvasViewer
                  url={pdfViewUrl}
                  title={`全体分析PDF: ${analysis.pdf.filename}`}
                  onLoadStateChange={setPdfRenderState}
                />
              )}
            </div>
          </section>
          </>
        )}

        {!analysis?.pdf && !awaitingDisclosure && (
          <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
            <p className="py-12 text-center text-sm text-gray-400">
              まだ全体分析PDFはアップロードされていません。
              {isAdmin && (
                <>
                  <br />
                  右上の「PDFをアップロード」から登録できます。
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
